export const STAMP_CLASS = "sfc-sig";
export const DEFAULT_STAMP_WIDTH = 140;
const MIN_STAMP_WIDTH = 40;
const MAX_STAMP_WIDTH = 480;

export interface StampLabels {
  remove: string;
  resize: string;
  image: string;
}

export interface PreviewMargin {
  topMm: number;
  sideMm: number;
}

export function stampStyles(margin: PreviewMargin): string {
  return `
@media screen {
  html, body { background: #fff; }
  body { margin: 0; padding: ${margin.topMm}mm ${margin.sideMm}mm; }
  body > .${STAMP_CLASS} { margin-left: ${margin.sideMm}mm; margin-top: ${margin.topMm}mm; }
  .${STAMP_CLASS} { outline: 1px dashed #1677ff; }
}
.${STAMP_CLASS} { position: absolute; z-index: 5; touch-action: none; cursor: move; user-select: none; -webkit-user-select: none; }
.${STAMP_CLASS} img { display: block; width: 100%; height: auto; pointer-events: none; }
.${STAMP_CLASS}-x { position: absolute; top: -14px; right: -14px; width: 28px; height: 28px; border-radius: 50%; border: 0; padding: 0; background: #ff4d4f; color: #fff; font-size: 18px; line-height: 28px; text-align: center; cursor: pointer; touch-action: none; }
.${STAMP_CLASS}-r { position: absolute; right: -10px; bottom: -10px; width: 20px; height: 20px; border-radius: 4px; background: #1677ff; cursor: nwse-resize; touch-action: none; }
@media print {
  .${STAMP_CLASS}-x, .${STAMP_CLASS}-r { display: none !important; }
  .${STAMP_CLASS} { outline: none !important; }
}`;
}

export function injectIntoHead(html: string, extra: string): string {
  const index = html.search(/<\/head>/i);
  if (index === -1) return `${extra}${html}`;
  return `${html.slice(0, index)}${extra}${html.slice(index)}`;
}

function anchorAt(doc: Document, x: number, y: number): HTMLElement {
  const hit = doc
    .elementsFromPoint(x, y)
    .find((el) => !(el as HTMLElement).closest(`.${STAMP_CLASS}`)) as HTMLElement | undefined;
  const cell = hit?.closest("td,th") as HTMLElement | null;
  return cell ?? doc.body;
}

function attach(doc: Document, stamp: HTMLElement, centerX: number, centerY: number) {
  const anchor = anchorAt(doc, centerX, centerY);
  const view = doc.defaultView;
  if (anchor !== doc.body && view?.getComputedStyle(anchor).position === "static") {
    anchor.style.position = "relative";
  }
  if (anchor === doc.body) doc.body.style.position = "relative";
  const rect = anchor.getBoundingClientRect();
  const style = view?.getComputedStyle(anchor);
  const padLeft = anchor === doc.body ? parseFloat(style?.paddingLeft || "0") : 0;
  const padTop = anchor === doc.body ? parseFloat(style?.paddingTop || "0") : 0;
  const width = stamp.offsetWidth;
  const height = stamp.offsetHeight;
  anchor.appendChild(stamp);
  stamp.style.left = `${centerX - rect.left - padLeft - width / 2}px`;
  stamp.style.top = `${centerY - rect.top - padTop - height / 2}px`;
}

function trackPointer(
  target: HTMLElement,
  start: PointerEvent,
  onMove: (dx: number, dy: number) => void,
  onEnd: (ev: PointerEvent) => void,
) {
  try {
    target.setPointerCapture(start.pointerId);
  } catch {}
  const move = (ev: PointerEvent) => onMove(ev.clientX - start.clientX, ev.clientY - start.clientY);
  const up = (ev: PointerEvent) => {
    target.removeEventListener("pointermove", move);
    target.removeEventListener("pointerup", up);
    target.removeEventListener("pointercancel", up);
    onEnd(ev);
  };
  target.addEventListener("pointermove", move);
  target.addEventListener("pointerup", up);
  target.addEventListener("pointercancel", up);
}

export function placeStamp(
  doc: Document,
  image: string,
  centerX: number,
  centerY: number,
  labels: StampLabels,
  width = DEFAULT_STAMP_WIDTH,
): HTMLElement {
  const stamp = doc.createElement("div");
  stamp.className = STAMP_CLASS;
  stamp.dataset.testid = "print-signature-stamp";
  stamp.style.width = `${width}px`;
  stamp.style.visibility = "hidden";

  const img = doc.createElement("img");
  img.src = image;
  img.alt = labels.image;
  img.draggable = false;

  const remove = doc.createElement("button");
  remove.type = "button";
  remove.className = `${STAMP_CLASS}-x`;
  remove.setAttribute("aria-label", labels.remove);
  remove.textContent = "×";
  remove.addEventListener("pointerdown", (ev) => ev.stopPropagation());
  remove.addEventListener("click", (ev) => {
    ev.stopPropagation();
    stamp.remove();
  });

  const resize = doc.createElement("div");
  resize.className = `${STAMP_CLASS}-r`;
  resize.setAttribute("aria-label", labels.resize);
  resize.addEventListener("pointerdown", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const startWidth = stamp.offsetWidth;
    trackPointer(
      resize,
      ev,
      (dx) => {
        const next = Math.min(MAX_STAMP_WIDTH, Math.max(MIN_STAMP_WIDTH, startWidth + dx));
        stamp.style.width = `${next}px`;
      },
      () => undefined,
    );
  });

  stamp.addEventListener("pointerdown", (ev) => {
    ev.preventDefault();
    const startLeft = parseFloat(stamp.style.left || "0");
    const startTop = parseFloat(stamp.style.top || "0");
    trackPointer(
      stamp,
      ev,
      (dx, dy) => {
        stamp.style.left = `${startLeft + dx}px`;
        stamp.style.top = `${startTop + dy}px`;
      },
      () => {
        const rect = stamp.getBoundingClientRect();
        attach(doc, stamp, rect.left + rect.width / 2, rect.top + rect.height / 2);
      },
    );
  });

  stamp.append(img, remove, resize);
  doc.body.appendChild(stamp);

  const finish = () => {
    attach(doc, stamp, centerX, centerY);
    stamp.style.visibility = "";
  };
  if (img.complete && img.naturalWidth) finish();
  else {
    img.addEventListener("load", finish, { once: true });
    img.addEventListener("error", finish, { once: true });
  }
  return stamp;
}

export function stampCount(doc: Document | null | undefined): number {
  return doc ? doc.querySelectorAll(`.${STAMP_CLASS}`).length : 0;
}
